import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { Drawer, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import I18n from '../../../../components/shared/i18n/I18n.component';
import Typography from '../../../../components/shared/Typography/Typography.component';
import colors from '../../../../config/colors';
import { i18nService } from '../../../../services/i18n.service';
import { snackbarService } from '../../../../services/snackbar.service';
import {
	fetchInvitationHistory,
	InvitationHistoryResponse,
} from './InviteHistoryDrawer.api';
import { sendUserInvitations } from '../InviteUsersDrawer/InviteUsersDrawer.api';
import InviteHistoryList from './InviteHistoryList.component';
import InviteHistoryEmpty from './InviteHistoryEmpty.component';

type InviteHistoryDrawerProps = {
	open: boolean;
	/** the table row the menu was opened from - null while the drawer is closed */
	user: any;
	/** scopes an invitation sent from the empty state to a business unit */
	unitId: number;
	onClose: () => void;
};

const useStyles = makeStyles({
	paper: {
		width: 450,
		maxWidth: '100%',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		padding: 24,
		boxSizing: 'border-box',
		overflow: 'hidden',
	},
});

/**
 * Side sheet listing every invitation the portal sent to a single user, newest
 * first. The history is fetched on every open - a cached list would be stale
 * right after an invitation is sent from the empty state.
 */
const InviteHistoryDrawer = ({
	open,
	user,
	unitId,
	onClose,
}: InviteHistoryDrawerProps) => {
	const classes = useStyles();

	const [history, setHistory] = useState<InvitationHistoryResponse | null>(
		null,
	);
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);

	/** ignore a response that arrives after the drawer was closed/reopened */
	const requestId = useRef(0);

	const legalId = user?.teudatZeut;

	const loadHistory = () => {
		const currentRequest = ++requestId.current;
		setHistory(null);
		setLoading(true);

		return fetchInvitationHistory(legalId)
			.then(res => {
				if (currentRequest !== requestId.current) {
					return;
				}
				setHistory(res || { userId: legalId, totalCount: 0, history: [] });
				setLoading(false);
			})
			.catch(() => {
				if (currentRequest !== requestId.current) {
					return;
				}
				snackbarService.show({
					message: i18nService.translate(
						'usersManagement.inviteHistory.loadErrorToast',
					),
					success: false,
				});
				// a failed load must never fall through to the empty template - its
				// send button would invite a user who may already have a history we
				// just could not read
				setLoading(false);
				onClose();
			});
	};


	useEffect(() => {
		if (!open || !legalId) {
			return;
		}
		// the drawer is reused across rows - a send left in flight by the previous
		// one must not keep this row's button disabled
		setSending(false);
		loadHistory();
		// a response landing after the drawer closed must not toast or re-close it
		return () => {
			requestId.current++;
		};
	}, [open, legalId]);

	const onSend = () => {
		setSending(true);

		sendUserInvitations([legalId], unitId)
			.then(() => {
				snackbarService.show({
					message: i18nService.translate(
						'usersManagement.inviteHistory.successToast',
					),
					success: true,
				});
				setSending(false);
				// the sheet now has a history to show - ask the server for it
				loadHistory();
			})
			.catch(() => {
				snackbarService.show({
					message: i18nService.translate(
						'usersManagement.inviteHistory.errorToast',
					),
					success: false,
				});
				setSending(false);
			});
	};

	return (
		<Drawer
			anchor='left'
			open={open}
			onClose={onClose}
			classes={{ paper: classes.paper }}
		>
			<View style={styles.header}>
				<I18n size={20} color='primaryDarkBlue' font='bold'>
					usersManagement.inviteHistory.title
				</I18n>
				<TouchableOpacity
					onPress={onClose}
					hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
				>
					<CloseIcon style={{ fontSize: 20, color: colors.greyText }} />
				</TouchableOpacity>
			</View>

			<Typography
				size={17}
				color='primaryDarkBlue'
				style={styles.subtitle}
				numberOfLines={1}
			>
				{user ? `${user.fullName} (${user.teudatZeut})` : ''}
			</Typography>

			{/* only this region scrolls - the header and the subtitle stay pinned */}
			<View style={styles.body}>
				{loading || !history ? (
					<ActivityIndicator color={colors.primary} />
				) : history.totalCount > 0 ? (
					<InviteHistoryList entries={history.history || []} />
				) : (
					<InviteHistoryEmpty
						onSend={onSend}
						sending={sending}
						activeUser={!!user?.status}
					/>
				)}
			</View>
		</Drawer>
	);
};

const styles = StyleSheet.create({
	header: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexShrink: 0,
	},
	subtitle: {
		marginTop: 16,
		flexShrink: 0,
	},
	// minHeight lets the region shrink so it scrolls instead of pushing the header out
	body: {
		flex: 1,
		minHeight: 0,
		marginTop: 24,
	},
});

export default InviteHistoryDrawer;
